import { mapDiscoverFiltersActive, MAP_DATE_PRESET_IDS } from './discoverEventFilters';

/**
 * Chip aktywnego filtra mapy (identyfikator + krótka etykieta).
 * @typedef {{ id: string, label: string }} MapActiveFilterChip
 */

/**
 * @param {object} p
 * @param {object | null} p.searchCenter
 * @param {string} p.searchAddress
 * @param {number|string} p.searchRange
 * @param {object} p.mapDiscoverFilters
 * @param {string} [p.eventCategory] — np. z „Odkrywaj według kategorii”; `'all'` lub puste = brak chipa
 * @param {(key: string, opts?: object, fallback?: string) => string} t
 * @returns {MapActiveFilterChip[]}
 */
export function buildMapActiveFilterChips(
  { searchCenter, searchAddress, searchRange, mapDiscoverFilters, eventCategory },
  t
) {
  /** @type {MapActiveFilterChip[]} */
  const chips = [];

  const r = Number(searchRange);
  if (searchCenter && Number.isFinite(r) && r > 0) {
    const place = (typeof searchAddress === 'string' && searchAddress.trim()) || t('discover.map.searchUnknownPlace', 'wybranej lokalizacji');
    chips.push({
      id: 'search-area',
      label: t('discover.map.activeFilterChips.searchShort', '{{place}} · {{radius}} km', {
        place,
        radius: Number.isFinite(r) ? String(r) : '',
      }),
    });
  }

  const cat = typeof eventCategory === 'string' ? eventCategory.trim().toLowerCase() : '';
  if (cat && cat !== 'all') {
    chips.push({
      id: 'eventCategory',
      label: t(`discover.popularCategories.names.${cat}`, cat),
    });
  }

  const f = mapDiscoverFilters || {};
  if (!mapDiscoverFiltersActive(f)) {
    return chips;
  }

  if (f.datePreset && MAP_DATE_PRESET_IDS.includes(f.datePreset)) {
    chips.push({
      id: 'datePreset',
      label: t(`discover.map.datePreset.${f.datePreset}`),
    });
  }

  if (f.dateFrom) {
    chips.push({
      id: 'dateFrom',
      label: t('discover.map.activeFilterChips.dateFrom', 'Od {{date}}', { date: f.dateFrom }),
    });
  }
  if (f.dateTo) {
    chips.push({
      id: 'dateTo',
      label: t('discover.map.activeFilterChips.dateTo', 'Do {{date}}', { date: f.dateTo }),
    });
  }

  const rawMin = f.priceMin != null && f.priceMin !== '' ? Number(f.priceMin) : NaN;
  const rawMax = f.priceMax != null && f.priceMax !== '' ? Number(f.priceMax) : NaN;
  const priceMin = Number.isFinite(rawMin) ? rawMin : null;
  const priceMax = Number.isFinite(rawMax) ? rawMax : null;
  if (priceMin != null && priceMax != null) {
    chips.push({
      id: 'price',
      label: t('discover.map.activeFilterChips.priceRange', '{{min}}–{{max}} zł/os.', { min: priceMin, max: priceMax }),
    });
  } else if (priceMin != null) {
    chips.push({
      id: 'price',
      label: t('discover.map.activeFilterChips.priceMin', '≥ {{min}} zł/os.', { min: priceMin }),
    });
  } else if (priceMax != null) {
    chips.push({
      id: 'price',
      label: t('discover.map.activeFilterChips.priceMax', '≤ {{max}} zł/os.', { max: priceMax }),
    });
  }

  if (f.isFree === true) {
    chips.push({
      id: 'pricing',
      label: t('discover.map.activeFilterChips.free', 'Bezpłatne'),
    });
  } else if (f.isFree === false) {
    chips.push({
      id: 'pricing',
      label: t('discover.map.activeFilterChips.paid', 'Płatne'),
    });
  }

  if (f.minParticipants) {
    const min = parseInt(String(f.minParticipants), 10);
    if (!Number.isNaN(min)) {
      chips.push({
        id: 'participantsMin',
        label: t('discover.map.activeFilterChips.partMin', 'Miejsc ≥ {{n}}', { n: min }),
      });
    }
  }
  if (f.maxParticipants) {
    const max = parseInt(String(f.maxParticipants), 10);
    if (!Number.isNaN(max)) {
      chips.push({
        id: 'participantsMax',
        label: t('discover.map.activeFilterChips.partMax', 'Miejsc ≤ {{n}}', { n: max }),
      });
    }
  }

  if (f.friendsOnly) {
    chips.push({
      id: 'friends',
      label: t('discover.map.activeFilterChips.friends', 'Znajomi'),
    });
  }
  if (f.mineOnly) {
    chips.push({
      id: 'mine',
      label: t('discover.map.activeFilterChips.mine', 'Moje'),
    });
  }

  return chips;
}
